import { MyServiceResponseBody, WorkingLog, WorkingLogStatus } from "@src/model/serviceModel";
import {
	FixMatchRequestBudget,
	ReservationMatchRequest,
	SubmitMatch,
} from "@src/model/signupAndApplyModel";
import { MatchStage } from "@src/utils/types/stage";
import { AxiosResponse } from "axios";
import { apiServiceV2 } from ".";
import { ExpertEvaluationData } from "./evaluation";
import { ApprovalProposalRequest, ProposalResponse, SuccessMessage } from "./types";
import { UserInfo } from "@src/types/userInfo";

export const fixMatchRequestBudget: (
	data: FixMatchRequestBudget & {
		matchUuid: string;
	},
) => Promise<void> = async (data) => {
	await apiServiceV2.post("/match/screening/fix", data);
};

export const submitMatch = async (
	data: SubmitMatch,
): Promise<{ matchUuid: string; matchScore: number }> => {
	const response: AxiosResponse<{ matchUuid: string; matchScore: number }> =
		await apiServiceV2.post("/match", data);
	return response.data;
};

export const getExpertUserInfo = async (): Promise<UserInformation> => {
	const response: AxiosResponse<UserInformation> = await apiServiceV2.get(`/userInfo/expert`);
	return response.data;
};

export const setExpertUserInfo = async (data: UserInformation): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/userInfo/expert`,
		data,
	);
	return response.data;
};

export const endProject = async (data: {
	matchUuid: string;
	endDate: Date;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/project/complete`,
		{
			matchId: data.matchUuid,
			date: data.endDate,
		},
	);
	return response.data;
};

export const getNotStartedMatchUuid = async (): Promise<{
	matchUuid: string | null;
	promotion: string | null;
	matchBudget: string | null;
	matchMonthBudget: string | null;
	dateIntro: Date | null;
	revivedMatch: boolean | null;
	matchScore: number | null;
	job: string | null;
}> => {
	const response: AxiosResponse<{
		matchUuid: string | null;
		dateIntro: Date | null;
		promotion: string | null;
		matchBudget: string | null;
		matchMonthBudget: string | null;
		revivedMatch: boolean | null;
		matchScore: number | null;
		job: string | null;
	}> = await apiServiceV2.get("/match/status/notStarted");
	return response.data;
};

export interface CreateMatchFromReferenceRequest {
	userInfo: UserInfo;
	matchUuid: string;
	referenceMatchUuid: string;
}

export interface CreateMatchFromReferenceResponse {
	isSuccess: boolean;
	isExistingUser?: boolean;
	matchUuid?: string;
	registerUrl?: string;
}

export const createMatchFromReference = async (
	data: CreateMatchFromReferenceRequest,
): Promise<CreateMatchFromReferenceResponse> => {
	const response: AxiosResponse<CreateMatchFromReferenceResponse> = await apiServiceV2.post(
		"/match/create-from-reference",
		data,
	);
	return response.data;
};

export const reservationMatch = async (
	data: ReservationMatchRequest,
): Promise<{ isSuccess: boolean }> => {
	//
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/match/consult",
		data,
	);
	return response.data;
};

export const reservationMatchIntro = async (
	data: ReservationMatchRequest,
): Promise<{ isSuccess: boolean }> => {
	//
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/match/intro",
		data,
	);
	return response.data;
};

export interface AcceptTestSession {
	matchedExpertDataUuid: string;
	interviewQuestionArray: string[];
	clientScheduleComment?: string;
}

export interface AcceptTestSessionResponse {
	isSuccess: boolean;
	requiresDeposit: boolean;
	message: string;
	matchUuid?: string;
}

export const acceptTestsession = async ({
	matchedExpertDataUuid,
	interviewQuestionArray,
	clientScheduleComment,
}: AcceptTestSession): Promise<AcceptTestSessionResponse> => {
	const response: AxiosResponse<AcceptTestSessionResponse> = await apiServiceV2.post(
		`/match/accept/test`,
		{
			matchedExpertDataUuid: matchedExpertDataUuid,
			interviewQuestionArray: interviewQuestionArray,
			clientScheduleComment: clientScheduleComment,
		},
	);
	return response.data;
};

export const requestInterviewReschedule = async (
	matchedExpertDataUuid: string,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/utils/interview/reschedule`,
		{
			matchedExpertDataUuid,
		},
	);
	return response.data;
};

export const requestInterviewRescheduleByClient = async (
	matchedExpertDataUuid: string,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/utils/interview/reschedule-client`,
		{
			matchedExpertDataUuid,
		},
	);
	return response.data;
};

interface RejectMatchDataObj {
	matchedExpertDataUuid: string;
	message: string;
}

interface RejectMatchDataByClientObj {
	matchedExpertDataUuid: string;
	reason?: string;
}

export const rejectTestsessionByClient = async (
	data: RejectMatchDataByClientObj,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/reject/testsession/by-client`,
		{
			matchedExpertDataUuid: data.matchedExpertDataUuid,
			reason: data.reason || "",
		},
	);
	return response.data;
};

export const cancelInterviewByClient = async (data: {
	matchedExpertDataUuid: string;
	reason: string;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/cancel/interview/by-client`,
		{
			matchedExpertDataUuid: data.matchedExpertDataUuid,
			reason: data.reason,
		},
	);
	return response.data;
};

export const cancelInterviewByExpert = async (data: {
	matchedExpertDataUuid: string;
	reason: string;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/cancel/interview/by-expert`,
		{
			matchedExpertDataUuid: data.matchedExpertDataUuid,
			reason: data.reason,
		},
	);
	return response.data;
};

export const rejectProposalByClient = async (data: {
	matchedExpertDataUuid: string;
	reason: string;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/reject/proposal/by-client`,
		{
			matchedExpertDataUuid: data.matchedExpertDataUuid,
			reason: data.reason,
		},
	);
	return response.data;
};

export const acceptMatch = async (data: {
	matchedExpertDataUuid: string;
	comment: string;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/accept`,
		{
			matchedExpertDataUuid: data.matchedExpertDataUuid,
			comment: data.comment,
		},
	);
	return response.data;
};

export const saveQuestionAnswersDraft = async (data: {
	matchUuid: string;
	questionAnswers: { question: string; answer: string }[];
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/save-question-answers-draft`,
		{
			matchUuid: data.matchUuid,
			questionAnswers: data.questionAnswers,
		},
	);
	return response.data;
};

export const applyOpenMatch = async (data: {
	matchUuid: string;
	comment: string;
	questionAnswers?: { question: string; answer: string }[];
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/apply`,
		{
			matchUuid: data.matchUuid,
			comment: data.comment,
			questionAnswers: data.questionAnswers,
		},
	);
	return response.data;
};

export const acceptMatchDirect = async (data: {
	matchedExpertDataUuid: number;
	comment: string;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/directs/match/accept`,
		{
			uuid: data.matchedExpertDataUuid,
			comment: data.comment,
		},
	);
	return response.data;
};

export const rejectMatch = async (data: RejectMatchDataObj): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/reject`,
		{
			matchedExpertDataUuid: data.matchedExpertDataUuid,
			message: data.message,
		},
	);
	return response.data;
};

export const confirmMatch = async (data: {
	matchUuid: string;
	expertQuestions?: string[];
	includeAgency?: boolean;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/confirm`,
		{
			matchUuid: data.matchUuid,
			expertQuestions: data.expertQuestions,
			includeAgency: data.includeAgency,
		},
	);
	return response.data;
};

export const deletePendingMatch = async (matchUuid: string): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.delete(
		`/match/${matchUuid}`,
	);
	return response.data;
};

export const profileView = async (data: { matchUuid: string }): Promise<void> => {
	await apiServiceV2.put("/match/profile/view", {
		uuid: data.matchUuid,
	});
};

export const kickoffView = async (data: { matchUuid: string }): Promise<void> => {
	await apiServiceV2.put("/match/kickoff/view", {
		uuid: data.matchUuid,
	});
};

export const getMatchRequestData = async (matchUuid: string): Promise<ApplicationData> => {
	const response: AxiosResponse<{ requestData: ApplicationData }> = await apiServiceV2.get(
		`/match/requestData?uuid=${matchUuid}`,
	);
	return response.data.requestData;
};

export const getProposalDetail = async (
	proposalUuid: string,
	matchedExpertDataUuid: string,
): Promise<ProposalResponse> => {
	const response: AxiosResponse<ProposalResponse> = await apiServiceV2.post("/proposal/detail", {
		uuid: proposalUuid,
		matchedExpertDataUuid: matchedExpertDataUuid,
	});
	return response.data;
};

export interface QuoteRecommendationRequest {
	matchUuid: string;
	matchedExpertDataUuid: string;
	proposalType: "시간제";
}

export interface QuoteRecommendationStatsRequest {
	matchUuid?: string;
	matchedExpertDataUuid?: string;
	proposalType?: "시간제";
	roleLabel?: string;
	experienceBandLabel?: string;
	timeHours?: number;
}

export interface QuoteRecommendationStatsTableRow {
	roleLabel: string;
	experienceBandLabel: string;
	timeHours: number;
	count: number;
	avgBudgetKrw: number | null;
	filteredAvgBudgetKrw: number | null;
	medianBudgetKrw: number | null;
	minBudgetKrw: number | null;
	maxBudgetKrw: number | null;
	avgExpertFeeKrw: number | null;
}

export interface QuoteRecommendationDistributionBucket {
	label: string;
	minBudgetKrw: number;
	maxBudgetKrw: number;
	count: number;
}

export interface QuoteRecommendationDistribution {
	timeHours: number;
	sampleCount: number;
	filteredAvgBudgetKrw: number | null;
	filteredMedianBudgetKrw: number | null;
	filteredMinBudgetKrw: number | null;
	filteredMaxBudgetKrw: number | null;
	valuesKrw: number[];
	buckets: QuoteRecommendationDistributionBucket[];
}

export interface QuoteRecommendationStatsResponse {
	roleLabel: string;
	experienceBandLabel: string;
	availableRoleLabels: string[];
	availableExperienceBandLabels: string[];
	availableTimeHours: number[];
	rows: QuoteRecommendationStatsTableRow[];
	distributions: QuoteRecommendationDistribution[];
}

export interface QuoteRecommendationTimeBandItem {
	timeBandHours: number;
	pricingTier: "LOW" | "MID" | "HIGH";
	recommendedClientPriceManwon: number;
	recommendedExpertFeeManwon: number;
	rangeMinManwon: number;
	rangeMaxManwon: number;
	confidence: "HIGH" | "MEDIUM" | "LOW";
	reasons: string[];
	comparablesSummary: {
		sampleCount: number;
		normalizedRole: string;
		timeBandHours: number;
		source: string;
		avgAcceptedPriceManwon: number | null;
		medianPriceManwon: number;
		minPriceManwon: number;
		maxPriceManwon: number;
	};
	guardrails: {
		isDataSparse: boolean;
		isClientBudgetBelowRange: boolean;
		isOutlierRisk: boolean;
		usedFallback: boolean;
	};
	featureSnapshot: {
		remoteType: string;
		scopeComplexity: string;
		expertLevel: number | null;
		openPrice: number | null;
		clientBudget: number | null;
	};
	adjustmentFactors: {
		increasing: string[];
		decreasing: string[];
	};
}

export interface QuoteRecommendationResponse {
	normalizedRole: string;
	statsTable: QuoteRecommendationStatsTableRow[];
	aiSummary?: string | null;
	recommendations: QuoteRecommendationTimeBandItem[];
}

export interface QuoteRecommendationFeedbackRequest {
	matchUuid: string;
	matchedExpertDataUuid: string;
	recommendedClientPriceManwon: number;
	submittedClientPriceManwon?: number;
	action: "VIEWED" | "SUBMITTED_AS_RECOMMENDED" | "SUBMITTED_WITH_EDITS" | "IGNORED";
}

export const getQuoteRecommendation = async (
	data: QuoteRecommendationRequest,
): Promise<QuoteRecommendationResponse> => {
	const response: AxiosResponse<QuoteRecommendationResponse> = await apiServiceV2.post(
		"/proposal/quote-recommendation",
		data,
	);
	return response.data;
};

export const getQuoteRecommendationStats = async (
	data: QuoteRecommendationStatsRequest,
): Promise<QuoteRecommendationStatsResponse> => {
	const response: AxiosResponse<QuoteRecommendationStatsResponse> = await apiServiceV2.post(
		"/proposal/quote-recommendation/stats",
		data,
	);
	return response.data;
};

export const logQuoteRecommendationFeedback = async (
	data: QuoteRecommendationFeedbackRequest,
): Promise<void> => {
	await apiServiceV2.post("/proposal/quote-recommendation/feedback", data);
};

export const acceptProposal = async (data: ApprovalProposalRequest): Promise<SuccessMessage> => {
	const response: AxiosResponse<SuccessMessage> = await apiServiceV2.post(
		"/proposal/accept",
		data,
	);
	return response.data;
};

export const cancelProposalAccept = async (data: {
	matchedExpertDataUuid: string;
	proposalUuid: string;
}): Promise<SuccessMessage> => {
	const response: AxiosResponse<SuccessMessage> = await apiServiceV2.post(
		"/proposal/accept/cancel",
		data,
	);
	return response.data;
};

export const getMatchClientAll = async (): Promise<GetMyMatchResponse> => {
	const response: AxiosResponse<GetMyMatchResponse> = await apiServiceV2.get("/match/me");
	return response.data;
};

export const getKickoffMatch = async (): Promise<GetMatchKickoffResponse> => {
	const response: AxiosResponse<GetMatchKickoffResponse> = await apiServiceV2.get(
		"/match/kickoff",
	);
	return response.data;
};

export const getMeetingLogMatch = async (): Promise<GetMatchKickoffResponse> => {
	const response: AxiosResponse<GetMatchKickoffResponse> = await apiServiceV2.get(
		"/work-log/meeting",
	);
	return response.data;
};

export const updateUseMeetingLog = async (data: {
	matchUuid: string;
	value: boolean;
}): Promise<void> => {
	await apiServiceV2.put("/match/use-meetinglog", {
		uuid: data.matchUuid,
		value: data.value,
	});
};

export const addKickoffData = async (data: MatchKickoffResult): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/match/kickoff",
		data,
	);
	return response.data;
};

export const getMyService = async (): Promise<MyServiceResponseBody> => {
	const response: AxiosResponse<MyServiceResponseBody> = await apiServiceV2.get(
		"/work-log/matchServices",
	);
	return response.data;
};

export const onEvaluationExpert = async (logUuid: string, score: number[]) => {
	try {
		await apiServiceV2.post("/work-log/evaluation", {
			logUuid,
			score,
		});
	} catch (error) {}
};

export const createOrUpdateMyWorkingLog = async (
	workingLog: WorkingLog,
	body: string,
	type: WorkingLogStatus,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<SuccessMessage> = await apiServiceV2.post("/work-log", {
		workingLog,
		body,
		type,
	});
	return response.data;
};

export const confirmMyWorkingLog = async (
	workingLog: WorkingLog,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<SuccessMessage> = await apiServiceV2.post("/work-log/confirm", {
		workingLog,
	});
	return response.data;
};

export const getOngoingMatch = async (): Promise<OnGoingMatchResponse> => {
	const response: AxiosResponse<OnGoingMatchResponse> = await apiServiceV2.get("/match/ongoing");
	return response.data;
};

export interface GetMatchListResponseItem {
	matchUuid: string;
	job: string;
	endTime: Date;
	client: string;
	time: string;
	expectedPrice: string;
	clientBudget?: number;
	comment: string;
	neededSkills: string;
	workType: string;
	workPurpose: string;
	workDetail: string;
	currentSituation?: string;
	expectedWorkload?: string;
	preferredSkills?: string;
	internalCollaborators?: string;
	monthlyAdBudget?: number | null;
	canProduceCreativeInternally?: "yes" | "no" | "undecided" | null;
	brandName: string;
	brandUrl: string;
	businessType: string;
	applyCount: number;
	isMe: boolean;
	remoteWork: string;
	expertComment?: string;
	expertQuestions?: string[];
	questionAnswers?: { question: string; answer: string }[];
	savedQuestionAnswers?: { question: string; answer: string }[];
}

export interface OpenMatchRequestData extends GetMatchListResponseItem {
	type: number;
}

export const getOpenMatchData = async (
	uuid: string,
	userType: 1 | 2 | number,
): Promise<GetMatchListResponseItem | null> => {
	try {
		return (await apiServiceV2.get(`/match/open/by-uuid?uuid=${uuid}&type=${userType}`))
			.data as GetMatchListResponseItem;
	} catch (error) {
		return null;
	}
};

export const getMatchList = async (): Promise<GetMatchListResponseItem[]> => {
	try {
		return (await apiServiceV2.get(`/match/list`)).data as GetMatchListResponseItem[];
	} catch (error) {
		return [];
	}
	// return [
	// 	{
	// 		matchUuid: "1",
	// 		job: "test1",
	// 		endTime: new Date("2025-02-10"),
	// 		client: "TestClient",
	// 		time: "주 N시간 ~ N시간",
	// 		expectedPrice: "400만원 / 주",
	// 		comment: "코멘트입니다\n코멘트입니다\n코멘트입니다\n코멘트입니다\n",
	// 		neededSkills: "전략 수립, 매체 운영, 소재 기획",
	// 		workType: "전략/디렉팅",
	// 		workPurpose: "마케팅",
	// 		workDetail:
	// 			"마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n",
	// 		brandName: "브랜드명",
	// 		brandUrl: "https://1point.kr",
	// 		businessType: "모바일웹",
	// 	},
	// 	{
	// 		matchUuid: "2",
	// 		job: "test2",
	// 		endTime: new Date("2025-02-08"),
	// 		client: "TestClient",
	// 		time: "주 N시간 ~ N시간",
	// 		expectedPrice: "400만원 / 주",
	// 		comment: "코멘트입니다\n코멘트입니다\n코멘트입니다\n코멘트입니다\n",
	// 		neededSkills: "전략 수립, 매체 운영, 소재 기획",
	// 		workType: "전략/디렉팅",
	// 		workPurpose: "마케팅",
	// 		workDetail:
	// 			"마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n",
	// 		brandName: "브랜드명",
	// 		brandUrl: "https://1point.kr",
	// 		businessType: "모바일웹",
	// 	},
	// 	{
	// 		matchUuid: "3",
	// 		job: "test3",
	// 		endTime: new Date("2025-02-11"),
	// 		client: "TestClient",
	// 		time: "주 N시간 ~ N시간",
	// 		expectedPrice: "400만원 / 주",
	// 		comment: "코멘트입니다\n코멘트입니다\n코멘트입니다\n코멘트입니다\n",
	// 		neededSkills: "전략 수립, 매체 운영, 소재 기획",
	// 		workType: "전략/디렉팅",
	// 		workPurpose: "마케팅",
	// 		workDetail:
	// 			"마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n마케팅 전략 수립 및 디렉팅\n",
	// 		brandName: "브랜드명",
	// 		brandUrl: "https://1point.kr",
	// 		businessType: "모바일웹",
	// 	},
	// ];
};

export interface OnGoingMatchResponse {
	matchUuidArray: string[];
}

export interface ApplicationData {
	title: string;
	currentSituation: string;
	workRequestDetail: string;
	workRequestDetailUrl: string;
	job: string;
	workType: string;
	workAmount: string;
	workPeriod: string;
	matchCondition: string;
	startToWork: string;
	wishExpertLevel: string;
	wishWorkHour: string;
	dateApplication: string;
	expertUuid: string;
	link: string;
	brand: string;
	buisnessType: string;
	industry: string;
	comment: string;
	neededSkills: string;
}

export interface ExpertDataItem {
	dateRecommend: string;
	dateRecommendToExpert: string;
	dateTestsession: string;
	dateRequestConfirmTestsession: string;
	expert: string;
	proposalId: string;
	dateRequestTestSession: string;
	expertUuid: string;
	dateRequestForProposal: Date | string | null;
	dateRequestConfirmProposal: string;
	dateAcceptProposal: Date | string | null;
	isLastOne: boolean;
	matchedExpertDataUuid: string;
	dateApplyInterview: string | null;
	interviewQuestionArray: string[];
	interviewScheduleCode?: string;
	isTestSessionReschedule?: boolean;
	isClientTestSessionReSchedule?: boolean;
	clientScheduleComment?: string | null;
	expertScheduleComment?: string | null;
	isClientScheduleRerequest?: boolean;
	expertRecommendationComment: string;
	expertComment: string;
	dateOut: Date | null;
	expertJob: string;
	isRecommended: boolean;
	urlInterview?: string;
	questionAnswers?: { question: string; answer: string }[];
}

export enum LogClientStatus {
	"이탈" = 0,
	"연장" = 1,
	"보류" = 2,
}

export interface MatchConsultant {
	consultantName: string;
	consultantRealName: string;
	consultantPhone: string;
	consultantEmail: string;
	contactLink: string;
	consultantType:
		| "Matching Consultant"
		| "Account Manager"
		| "Co-founder & CEO"
		| "Co-founder & COO";
}

export interface MyMatch {
	stage: string;
	dateIntro: string;
	stageId: MatchStage | number;
	detailStage: string;
	client: string;
	expertType: string;
	dateApplication: string;
	dateConsulting: string;
	dateStartMatch: string;
	dateEndMatch: string;
	matchUuid: string;
	notionUrl: string;
	serviceType: string;
	applicationData: ApplicationData;
	isCompleteContract?: boolean;
	isCompleteFirstInvoice?: boolean;
	isProject: boolean;
	serviceGroup: "시간제" | "프로젝트" | "";
	timeOption?: number | null;
	expertFee?: number | null;
	servicePrice?: number | null;
	currentLogSettlementDate: Date | null;
	lastLogUuid: string;
	firsetInvoiceUuid: string;
	clientLogPaymentIsPaid?: boolean | null;
	paymentStatusLabel?: string | null;
	useSupportFund?: boolean;
	lastLogStartDate: Date | null;
	lastLogStatus: LogClientStatus | null;
	expertDataList: ExpertDataItem[];
	dateViewProfile: Date | string | null;
	consultant: MatchConsultant | null;
	expertEvaluation: ExpertEvaluationData | null;
	serviceExpertName: string | null;
	logCount: number;
	isOpen: boolean;
	expertQuestions?: string[];
}

export interface GetMatchKickoffResponse {
	matchs: MatchKickoffData[];
}

export interface MultiSelectData {
	other?: string;
	value: boolean;
	label: string;
}

export interface MatchKickoffResult {
	expertName: string;
	clientName: string;
	count: number;
	logUuid: string;
	clientLogPaymentUuid?: string | null;
	clientLogPaymentIsPaid?: boolean | null;
	paymentStatusLabel?: string | null;
	useSupportFund?: boolean;
	isFuture?: boolean;
	/** 해당 회차(service_log)의 discount_rate가 5(5%)일 때 true */
	hasLongTermDiscount?: boolean;
	dateStart: Date | string | null;
	dateEnd: Date | string | null;
	workingTimeRange: string | null;
	communicationTimeRange: string | null;
	communicationChannel: MultiSelectData[];
	workingType: MultiSelectData[];
	workingStyle: MultiSelectData[];
	workingReportCycle: string | null;
	meetDate: Date | string | null;
	retrospect: string | null;
	nextPlan: string | null;
	isReadonly: boolean;
	dateViewKickoffmeeting: Date | string | null;
	workingLogs: WorkingLog[];
	showWrapup: boolean;
}

export interface MatchMeetingServiceData {
	timeOption?: number | null;
	servicePrice?: number | null;
	expertFee?: number | null;
	serviceType?: string | null;
	logDatas: MatchKickoffResult[];
}

export interface MatchKickoffData {
	logDatas: MatchKickoffResult[];
	serviceDatas?: MatchMeetingServiceData[];
	expertName: string;
	clientName: string;
	matchUuid: string;
	job: string;
	useMeetingLog: boolean;
	timeOption?: number | null; // 시간제 타입 (주 10시간, 15시간, 20시간 등)
	expertFee?: number | null; // 전문가 대금
	servicePrice?: number | null; // 서비스 대금
	serviceType?: string | null; // 서비스 유형 (시간제, 프로젝트 등)
}

export interface GetMyMatchResponse {
	matchs: MyMatch[];
}

export interface MatchItemLog {
	matchItemLogUuid: string;
	matchItemUuid: string;
	journal: string;
	serviceStartedAt: Date;
	serviceEndedAt: Date;
	unitAmount: number;
	isComplete: boolean;
	createdAt: Date;
}

export interface MatchItem {
	matchItemUuid: string;
	matchUuid: string;
	itemName: string;
	price: number;
	serviceType: number;
	createdAt: Date;
	matchItemLog: MatchItemLog[];
}

export interface User {
	nickname: string;
	userType: number;
}

export interface ExpertInfo {
	tempNickname: string;
	user: User;
}

export interface Match {
	matchUuid: string;
	workRequest?: any;
	isAffiliate: boolean;
	createdAt: Date;
	matchItem: MatchItem[];
	expertInfo: ExpertInfo;
}

export interface GetMatchClientResponse {
	match: Match[];
}

export interface UserInformation {
	name?: string;
	nickname?: string;
	phone?: string;
	email?: string;
	bankAccountOwner?: string;
	bankNumber?: string;
	bankName?: string;
	companyName?: string;
	taxManager?: string;
	taxManagerEmail?: string;
	taxManagerPhone?: string;
}

// JD 수정 요청 타입 (백엔드 UpdateMatchJDRequest와 동일 키)
export interface UpdateMatchJDRequest {
	matchUuid: string;
	// 매칭 조건(필수)
	remoteAvailable: "possible" | "impossible" | "partial" | null;
	inPersonWorkFrequency?: string; // 비대면+대면 혼합 선택 시 대면 근무 희망 횟수 (선택)
	primaryWorkTime: string;
	expertBudget: string; // 원 단위 문자열 (화면에서는 만원 단위로 표시)
	// 기업 정보
	brandService?: string;
	website?: string;
	businessType?: string;
	// 요청 업무
	workType?: string;
	requestedTask?: string;
	expectedWorkload?: string;
	workGoal?: string;
	currentSituation?: string;
	requiredSkills?: string;
	preferredSkills?: string;
	internalCollaborators?: string;
	monthlyAdBudget?: string; // 원 단위 문자열 (화면에서는 만원 단위로 표시)
	canProduceCreativeInternally?: "yes" | "no" | "undecided";
	additionalConsiderations?: string;
	expertQuestions?: string[];
}

export interface MatchJDAiChatMessage {
	role: "user" | "assistant";
	text: string;
}

export interface MatchJDAiSuggestion {
	remoteAvailable: "possible" | "impossible" | "partial" | null;
	inPersonWorkFrequency: string;
	primaryWorkTime: string;
	expertBudget: string;
	brandService: string;
	website: string;
	businessType: string;
	workGoal: string;
	currentSituation: string;
	expectedWorkload: string;
	requiredSkills: string;
	preferredSkills: string;
	internalCollaborators: string;
	additionalConsiderations: string;
	missingFields: string[];
	warnings: string[];
	summary: string;
}

export interface MatchJDAiChatRequest {
	matchUuid: string;
	messages?: MatchJDAiChatMessage[];
	currentForm?: Partial<UpdateMatchJDRequest>;
}

export interface MatchJDAiSuggestRequest {
	matchUuid: string;
	messages?: MatchJDAiChatMessage[];
	currentForm?: Partial<UpdateMatchJDRequest>;
}

export interface MatchJDAiSessionState {
	messages?: MatchJDAiChatMessage[];
	suggestion?: MatchJDAiSuggestion | null;
	suggestionBaseFormSnapshot?: Partial<UpdateMatchJDRequest> | null;
	appliedSuggestionFieldKeys?: Record<string, boolean>;
	updatedAt?: string;
}

export interface MatchJDAiSessionSaveRequest {
	matchUuid: string;
	session?: MatchJDAiSessionState | null;
}

export const updateMatchJD = async (
	data: UpdateMatchJDRequest,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.put(
		"/match/jd",
		data,
	);
	return response.data;
};

export const chatMatchJDAi = async (
	data: MatchJDAiChatRequest,
): Promise<{ isSuccess: boolean; assistantMessage: string }> => {
	const response: AxiosResponse<{ isSuccess: boolean; assistantMessage: string }> =
		await apiServiceV2.post("/match/jd-ai/chat", data);
	return response.data;
};

export const suggestMatchJDAi = async (
	data: MatchJDAiSuggestRequest,
): Promise<{ isSuccess: boolean; suggestion: MatchJDAiSuggestion }> => {
	const response: AxiosResponse<{ isSuccess: boolean; suggestion: MatchJDAiSuggestion }> =
		await apiServiceV2.post("/match/jd-ai/suggest", data);
	return response.data;
};

export const getMatchJDAiSession = async (
	matchUuid: string,
): Promise<{ isSuccess: boolean; session: MatchJDAiSessionState | null }> => {
	const response: AxiosResponse<{ isSuccess: boolean; session: MatchJDAiSessionState | null }> =
		await apiServiceV2.get(`/match/jd-ai/session/${matchUuid}`);
	return response.data;
};

export const saveMatchJDAiSession = async (
	data: MatchJDAiSessionSaveRequest,
): Promise<{ isSuccess: boolean; session: MatchJDAiSessionState | null }> => {
	const response: AxiosResponse<{ isSuccess: boolean; session: MatchJDAiSessionState | null }> =
		await apiServiceV2.post("/match/jd-ai/session", data);
	return response.data;
};

export const resetMatchJDAiSession = async (matchUuid: string): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.delete(
		`/match/jd-ai/session/${matchUuid}`,
	);
	return response.data;
};

export const generateExpertQuestions = async (
	matchUuid: string,
	existingQuestions?: string[],
): Promise<{ isSuccess: boolean; questions: string[] }> => {
	const response: AxiosResponse<{ isSuccess: boolean; questions: string[] }> =
		await apiServiceV2.post("/match/generate-questions", { matchUuid, existingQuestions });
	return response.data;
};

export type ProfileAnalysisRecommendationLevel = "HIGH" | "MEDIUM" | "LOW";

export interface MatchProfileAnalysisExpertReport {
	matchedExpertDataUuid: string;
	expertInfoUuid: string;
	expertName: string;
	expertJob: string;
	suitabilityVerdict: "FIT" | "PARTIAL_FIT" | "NOT_FIT";
	suitabilityReason: string;
	evidenceDetails: string[];
	evidenceDetailSources?: {
		factId?: string;
		detail: string;
		sourceType: string;
		sourceField: string;
		sourceLabel: string;
	}[];
}

export interface MatchProfileAnalysisReport {
	summary: {
		headline: string;
		recommendationLevel: ProfileAnalysisRecommendationLevel;
		decisionGuide: string;
		generatedAt: string;
	};
	experts: MatchProfileAnalysisExpertReport[];
	comparison: {
		interviewPriority: string[];
		decisionTips: string[];
	};
	recommendationType: "INFORMATIONAL";
}

export interface MatchProfileAnalysisGetResponse {
	isSuccess: boolean;
	exists: boolean;
	sourceHash?: string;
	promptVersion?: string;
	model?: string | null;
	responseId?: string | null;
	generatedAt?: string | null;
	report?: MatchProfileAnalysisReport | null;
}

export interface MatchProfileAnalysisGenerateResponse {
	isSuccess: boolean;
	fromCache?: boolean;
	sourceHash?: string;
	promptVersion?: string;
	model?: string | null;
	responseId?: string | null;
	generatedAt?: string | null;
	report?: MatchProfileAnalysisReport | null;
	errorCode?: string;
	message?: string;
}

export const getMatchProfileAnalysis = async (
	matchUuid: string,
): Promise<MatchProfileAnalysisGetResponse> => {
	const response: AxiosResponse<MatchProfileAnalysisGetResponse> = await apiServiceV2.get(
		`/match/profile-analysis/${matchUuid}`,
	);
	return response.data;
};

export const generateMatchProfileAnalysis = async (data: {
	matchUuid: string;
	forceRegenerate?: boolean;
}): Promise<MatchProfileAnalysisGenerateResponse> => {
	const response: AxiosResponse<MatchProfileAnalysisGenerateResponse> = await apiServiceV2.post(
		"/match/profile-analysis/generate",
		data,
		{
			// 프로필 분석 생성은 OpenAI 호출/전문가 수에 따라 60초를 초과할 수 있으므로
			// 기본 axios timeout(60000ms)을 해제한다.
			timeout: 0,
		},
	);
	return response.data;
};

// ── 제안서 수락 어시스턴트 AI ──

export interface MatchProposalAssistantExpertReport {
	matchedExpertDataUuid: string;
	expertInfoUuid: string;
	expertName: string;
	expertJob: string;
	expertHeadline?: string;
	expertDecisionGuide?: string;
	consultantComment: string;
	suitabilityVerdict: "FIT" | "PARTIAL_FIT" | "NOT_FIT";
	suitabilityReason: string;
	proposalHighlights: string;
	interviewInsights: string;
	evidenceDetails: string[];
	evidenceDetailSources?: {
		factId?: string;
		detail: string;
		sourceType: string;
		sourceField: string;
		sourceLabel: string;
	}[];
}

export interface MatchProposalAssistantReport {
	summary: {
		headline: string;
		recommendationLevel: ProfileAnalysisRecommendationLevel;
		decisionGuide: string;
		generatedAt: string;
	};
	experts: MatchProposalAssistantExpertReport[];
	comparison: {
		acceptPriority: string[];
		decisionTips: string[];
	};
	overallAssessment?: string;
	overallAssessmentGeneratedAt?: string;
	recommendationType: "INFORMATIONAL";
}

export interface MatchProposalAssistantOverallResponse {
	isSuccess: boolean;
	fromCache?: boolean;
	overallAssessment?: string;
	report?: MatchProposalAssistantReport | null;
	errorCode?: string;
	message?: string;
}

export interface MatchProposalAssistantGetResponse {
	isSuccess: boolean;
	exists: boolean;
	sourceHash?: string;
	promptVersion?: string;
	model?: string | null;
	responseId?: string | null;
	generatedAt?: string | null;
	report?: MatchProposalAssistantReport | null;
	hasDeadlineSet?: boolean;
}

export interface MatchProposalAssistantGenerateResponse {
	isSuccess: boolean;
	fromCache?: boolean;
	sourceHash?: string;
	promptVersion?: string;
	model?: string | null;
	responseId?: string | null;
	generatedAt?: string | null;
	report?: MatchProposalAssistantReport | null;
	errorCode?: string;
	message?: string;
}

export const getMatchProposalAssistant = async (
	matchUuid: string,
): Promise<MatchProposalAssistantGetResponse> => {
	const response: AxiosResponse<MatchProposalAssistantGetResponse> = await apiServiceV2.get(
		`/match/proposal-assistant/${matchUuid}`,
	);
	return response.data;
};

export const generateMatchProposalAssistant = async (data: {
	matchUuid: string;
	matchedExpertDataUuid?: string;
	forceRegenerate?: boolean;
}): Promise<MatchProposalAssistantGenerateResponse> => {
	const response: AxiosResponse<MatchProposalAssistantGenerateResponse> = await apiServiceV2.post(
		"/match/proposal-assistant/generate",
		data,
		{
			timeout: 0,
		},
	);
	return response.data;
};

export const generateOverallAssessment = async (data: {
	matchUuid: string;
	forceRegenerate?: boolean;
}): Promise<MatchProposalAssistantOverallResponse> => {
	const response: AxiosResponse<MatchProposalAssistantOverallResponse> = await apiServiceV2.post(
		"/match/proposal-assistant/generate-overall",
		data,
		{
			timeout: 0,
		},
	);
	return response.data;
};

export const sendProposalAssistantFeedback = async (data: {
	matchUuid: string;
	rating: number;
	comment?: string;
}): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/match/proposal-assistant/feedback",
		data,
	);
	return response.data;
};

// 전문가 매칭 포기
export const abandonMatchByExpert = async (
	matchId: string,
	reason: string,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		`/match/${matchId}/expert-abandon`,
		{ reason },
	);
	return response.data;
};

// 일정 선택 재요청
export const requestScheduleRerequest = async (
	matchedExpertDataUuid: string,
	clientScheduleComment?: string,
): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/utils/interview/schedule-rerequest",
		{
			matchedExpertDataUuid,
			clientScheduleComment,
		},
	);
	return response.data;
};

// 지원자 없음 — 추가 진행 희망
export const noApplicantExtend = async (matchUuid: string): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/match/no-applicant/extend",
		{ uuid: matchUuid },
	);
	return response.data;
};

// 지원자 없음 — 추가 진행 미희망
export const noApplicantClose = async (matchUuid: string): Promise<{ isSuccess: boolean }> => {
	const response: AxiosResponse<{ isSuccess: boolean }> = await apiServiceV2.post(
		"/match/no-applicant/close",
		{ uuid: matchUuid },
	);
	return response.data;
};
